import { useEffect, useRef } from "react"
import { useColorScheme } from "@mui/material/styles"
import { useSelector } from "react-redux"
import { OK } from "helpers/data-statuses"
import { useThemePreference } from "hooks/use-theme-preference"
import { useAreThemesEnabled, useAccountAlias } from "resources/account"
import { useIsSuperuser } from "hooks/users"
import { getNeedSignTerms } from "modules/account-root/selectors"
import {
  applyMuiSchemeFromPreference,
  THEME_PREFERENCE_KEY,
} from "../../../styles/theme-mode-config"

function persistThemePreference(value: string) {
  try {
    localStorage.setItem(THEME_PREFERENCE_KEY, value)
  } catch {
    // ignore
  }
}

function ThemePreferenceSyncActive() {
  const { setMode, setColorScheme } = useColorScheme()
  const { storedMode, preferenceStatus } = useThemePreference()
  const accountAlias = useAccountAlias()
  const areThemesEnabled = useAreThemesEnabled()
  const isSuperuser = useIsSuperuser()
  const appliedRef = useRef(false)
  const lastAccountRef = useRef<string | null>(null)
  const wereThemesDisabledRef = useRef(false)
  const canUseThemes = areThemesEnabled || isSuperuser

  useEffect(() => {
    if (accountAlias !== lastAccountRef.current) {
      lastAccountRef.current = accountAlias
      appliedRef.current = false
    }
    if (canUseThemes === true && wereThemesDisabledRef.current) {
      wereThemesDisabledRef.current = false
      appliedRef.current = false
    } else if (canUseThemes === false) {
      if (!wereThemesDisabledRef.current) {
        appliedRef.current = false
      }
      wereThemesDisabledRef.current = true
    }
  }, [accountAlias, areThemesEnabled, isSuperuser, canUseThemes])

  useEffect(() => {
    if (!accountAlias || appliedRef.current || areThemesEnabled === null) {
      return
    }

    if (canUseThemes === false) {
      setMode("light")
      setColorScheme({ light: "light" })
      persistThemePreference("light-colors")
      appliedRef.current = true
      return
    }

    if (preferenceStatus !== OK || !storedMode) {
      return
    }

    appliedRef.current = true
    applyMuiSchemeFromPreference(storedMode, { setMode, setColorScheme })
    persistThemePreference(storedMode)
  }, [accountAlias, canUseThemes, preferenceStatus, storedMode, setMode, setColorScheme, areThemesEnabled])

  return null
}

export default function ThemePreferenceSync() {
  const accountAlias = useAccountAlias()
  const needSignTerms = useSelector(getNeedSignTerms)

  if (!accountAlias || needSignTerms !== false) {
    return null
  }

  return <ThemePreferenceSyncActive />
}
